import { ChangeEvent } from 'react';
import { action, computed, IObservableArray, makeObservable, observable, reaction, runInAction, when } from 'mobx';
import { JobSpeciality } from '@clipr/lib';
import { JobModel } from '../../entities/job';
import { documentDragErrorMessage, isDocumentFile, Nullable, pluralize, SUPPORTED_DOCUMENT_TYPES_LABEL } from '../../core';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, InputState } from '../../components/input';
import { createArray } from '../../core/array';
import { JobSynchronizer } from '../../entities/job/jobSynchronizer';
import { jobIdFromToken, recordingLocationFromToken } from '../../services/upload/uploadToken';
import Routes, { BackRoutes } from '../../routes';
import { notifySuccess } from '../../services/notifications';
import { WarrantUploadFileItemState } from './warrantUploadFileItemState';

type Props = BindingProps<{
  teamId?: string;
  folderId?: string;
  speciality?: JobSpeciality;
}>;

export const MAX_LINKED_VIDEOS = 10;
export const MAX_RECORDS_HOLDERS = 10;
const MAX_HOLDER_CHARS = 5000; // the backend's cap on subpoenaRequest.recipient

const COMPANY_SUFFIX = /^(?:inc|incorporated|llc|l\.l\.c|ltd|limited|corp|corporation|co|company|n\.?a|national association|lp|l\.p|llp|pllc|plc|p\.c|pbc|fsb|f\.a|s\.a|gmbh|ag|b\.v|n\.v|ulc|u\.s\.a?|usa|d\/?b\/?a|a\.?k\.?a|f\/k\/a)\.?(?=$|[^a-z])/i;
// A street, ZIP, suite or attention part means a one-line address, which stays one holder.
const ADDRESS_PART = /^\d+\s+\S+\s+(?:st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|way|pkwy|parkway|ln|lane|ct|court|pl|place|hwy|highway|sq|square|cir|circle)\b|\b[A-Z]{2}\s+\d{5}(?:-\d{4})?$|^\d{5}(?:-\d{4})?$|^(?:suite|ste|apt|unit|floor|fl|room|p\.?\s?o\.?\s+box|attn|attention|c\/o|mail code)\b/i;

/**
 * "Chase, Venmo, Chime Financial, Inc." → three holders. Commas and semicolons separate holders,
 * except inside parentheses and before a company suffix. A multiline paste (a search.org entry)
 * or a one-line address stays whole. Wrong splits show in the form's preview, with "Keep as one".
 */
export function splitRecordsHolders(text: string): string[] {
  const value = text.trim();
  if (!value) return [];
  if (value.includes('\n')) return [value];
  const parts: string[] = [];
  let depth = 0, part = '';
  for (const ch of value) {
    depth = Math.max(0, depth + (ch === '(' ? 1 : ch === ')' ? -1 : 0));
    if ((ch === ',' || ch === ';') && !depth) {
      parts.push(part);
      part = '';
    } else part += ch;
  }
  const names = [...parts, part].map(name => name.trim().replace(/^(?:and|&)\s+/i, '')).filter(Boolean)
    .reduce<string[]>((acc, name) => {
      if (acc.length && COMPANY_SUFFIX.test(name)) acc[acc.length - 1] += `, ${name}`;
      else acc.push(name);
      return acc;
    }, []);
  return names.some(name => ADDRESS_PART.test(name)) ? [value] : names;
}

/** "Chime Financial, Inc." from a pasted search.org entry's first line. */
export function holderName(holder: string): string {
  return holder.split('\n')[0].replace(/^corporate name:\s*/i, '').trim();
}

export type WarrantDocumentType = 'primary' | 'supplemental';

export const WARRANT_DOCUMENT_TYPE_ITEMS = [
  { value: 'primary', label: 'Primary narrative' },
  { value: 'supplemental', label: 'Supplemental narrative' },
];

export class WarrantUploadState
  extends StoreNode {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      linkVideos: observable,
      speciality: computed,
      isSubpoena: computed,
      recordsHolders: computed,
      holdersError: computed,
      addHolder: action,
      isInvestigative: computed,
      toggleWhole: action,
      teamId: computed,
      folderId: computed,
      isHovered: observable,
      isDragHovered: observable,
      isSubmitting: observable,
      primaryItemId: observable,
      isDisabled: computed,
      showStagingForm: computed,
      isSubmitDisabled: computed,
      setLinkVideos: action,
      toggleLinkedVideo: action,
      setPrimary: action,
      setDocumentType: action,
      dragError: computed,
      dragErrorMessage: computed,
      dragDropMessage: computed,
      handlePointerEnter: action,
      handlePointerLeave: action,
      handleFileChange: action,
      handleFileClick: action,
      handleDragEnter: action,
      handleDragLeave: action,
      handleDrop: action,
      setUploadInput: action,
      addFileItem: action,
      removeFileItem: action,
      submit: action,
      reset: action,
    });

    this.detectiveInsightsInput = input(this, {
      name: 'detectiveInsights',
      label: `Add additional Detective insights`,
      placeholder: `Add additional Detective insights`,
      multiline: true,
    });
    this.addHolder();
    this.tagsInput = input(this, {
      name: 'tags',
      label: 'Tags',
    });
    reaction(() => this.teamId, () => this.setLinkVideos(false));
  }

  readonly holderInputs = observable.array<InputState>([], { deep: false });
  /** Names of the boxes the user keeps as one holder despite their commas. */
  readonly wholeHolders = observable.set<string>();
  private holderCount = 0;
  readonly tagsInput: InputState;
  readonly linkedVideoIds = observable.set<string>();
  readonly libraryVideos = observable.array<JobModel>([], { deep: false });
  linkVideos: boolean = false;

  get speciality(): JobSpeciality {
    return this.resolvedProps.speciality ?? JobSpeciality.WarrantFirstDraft;
  }

  get isSubpoena(): boolean {
    return this.speciality === JobSpeciality.SubpoenaRequestDraft;
  }

  holdersIn(holder: InputState): string[] {
    const value = holder.value?.trim() ?? '';
    return this.wholeHolders.has(holder.name) ? [value].filter(Boolean) : splitRecordsHolders(value);
  }

  get recordsHolders(): string[] {
    const seen = new Set<string>();
    return this.holderInputs.flatMap(holder => this.holdersIn(holder)).filter(holder => {
      const key = holder.toLowerCase();
      return !seen.has(key) && !!seen.add(key);
    });
  }

  get holdersError(): string | null {
    const extra = this.recordsHolders.length - MAX_RECORDS_HOLDERS;
    if (extra > 0)
      return `Up to ${MAX_RECORDS_HOLDERS} records holders: remove ${extra}.`;
    if (this.holderInputs.some(holder => holder.error))
      return `A records holder can be at most ${MAX_HOLDER_CHARS} characters.`;
    return null;
  }

  addHolder(value?: string, whole = false) {
    const holder = input(this, {
      name: `recipient-${this.holderCount++}`,
      label: 'Records holder',
      placeholder: 'e.g. Chase, Venmo, Chime Financial, Inc. (or paste one search.org entry per box)',
      multiline: true,
      charCountMax: MAX_HOLDER_CHARS,
    });
    if (value) holder.handleChange(null, value);
    if (whole) this.wholeHolders.add(holder.name);
    this.holderInputs.push(holder);
  }

  toggleWhole(holder: InputState) {
    if (!this.wholeHolders.delete(holder.name))
      this.wholeHolders.add(holder.name);
  }

  get isInvestigative(): boolean {
    return this.speciality === JobSpeciality.InvestigativeDraft;
  }

  readonly detectiveInsightsInput: InputState;

  get teamId(): Nullable<string> {
    return this.resolvedProps.teamId;
  }
  get folderId(): Nullable<string> {
    return this.resolvedProps.folderId;
  }

  isHovered: boolean = false;
  isDragHovered: boolean = false;
  isSubmitting: boolean = false;
  primaryItemId: Nullable<string> = null;
  readonly dragItems = observable.array<DataTransferItem>();

  readonly jobId: Nullable<string> = null;

  get isDisabled(): boolean {
    return this.isSubmitting;
  }

  readonly stagedItems: IObservableArray<WarrantUploadFileItemState> =
    createArray<WarrantUploadFileItemState>(true) as IObservableArray;

  get showStagingForm(): boolean {
    return this.stagedItems.length > 0;
  }

  get isSubmitDisabled(): boolean {
    return this.isSubmitting
      || this.stagedItems.length === 0
      || (this.isSubpoena && (this.recordsHolders.length === 0 || !!this.holdersError));
  }

  isPrimary(id: string): boolean {
    return this.primaryItemId === id;
  }

  documentTypeValue(id: string): WarrantDocumentType {
    return this.isPrimary(id) ? 'primary' : 'supplemental';
  }

  async setLinkVideos(link: boolean) {
    this.linkVideos = link;
    this.linkedVideoIds.clear();
    if (!link)
      return;
    const teamId = this.teamId;
    const [jobs, err] = await this.store.jobManager.apiFetchJobs({
      first: 100,
      filter: { teamId: teamId || undefined, status: { eq: 'Done' } },
    });
    if (err)
      this.dispatch('NotificationService', 'notifyError', 'Could not load this library\'s videos.');
    // Shared videos are listed too, but only the library's own can be linked.
    const owned = (job: JobModel) => (teamId ? job.ownerTeamId === teamId : job.ownerUserId === this.store.user?.id);
    runInAction(() => this.libraryVideos.replace((jobs ?? []).filter(job => !job.isDocumentJob && owned(job))));
  }

  toggleLinkedVideo(id: string) {
    if (this.linkedVideoIds.has(id))
      this.linkedVideoIds.delete(id);
    else if (this.linkedVideoIds.size < MAX_LINKED_VIDEOS)
      this.linkedVideoIds.add(id);
  }

  setPrimary(id: string) {
    this.primaryItemId = id;
  }

  // Exactly one patrol narrative. Promoting a file demotes the previous primary;
  // demoting the current primary promotes the next staged file so one always remains.
  setDocumentType(id: string, type: WarrantDocumentType) {
    if (type === 'primary') {
      this.primaryItemId = id;
      return;
    }
    if (this.primaryItemId === id) {
      const next = this.stagedItems.find(item => item.id !== id);
      this.primaryItemId = next?.id ?? null;
    }
  }

  get dragError(): boolean {
    return !!this.dragErrorMessage;
  }

  get dragErrorMessage(): Nullable<string> {
    return documentDragErrorMessage([...this.dragItems]);
  }

  get dragDropMessage(): Nullable<string> {
    const items = [...this.dragItems];
    if (items.length === 0)
      return `Drop files here`;
    return `Drop ${pluralize(items.length, 'file', 'files')} here`;
  }

  handlePointerEnter(_evt: PointerEvent) {
    this.isHovered = true;
  }
  handlePointerLeave(_evt: PointerEvent) {
    this.isHovered = false;
  }
  handleFileChange(evt: ChangeEvent<HTMLInputElement>) {
    this.setUploadInput(evt.target.files);
    // @ts-expect-error -- resetting input.value to null clears the file selection; string type doesn't allow null
    evt.target.value = null;
  }
  handleFileClick(_evt: PointerEvent) { /* noop */ }

  handleDragEnter(evt: DragEvent) {
    this.isDragHovered = true;
    const items = evt.dataTransfer?.items;
    if (items && items.length > 0) {
      this.dragItems.replace([...items]);
    }
  }
  handleDragLeave(_evt: DragEvent) {
    this.isDragHovered = false;
  }
  handleDrop(_evt: DragEvent) {
    this.isDragHovered = false;
    this.dragItems.clear();
  }

  setUploadInput(fileList?: FileList | null) {
    if (!fileList || fileList.length === 0)
      return;

    const inputFiles = [...fileList];

    const nonDocumentFiles = inputFiles.filter(file => !isDocumentFile(file));
    if (nonDocumentFiles.length > 0) {
      this.dispatch(
        'NotificationService',
        'notifyError',
        pluralize(
          nonDocumentFiles.length,
          `file is not a supported document (${SUPPORTED_DOCUMENT_TYPES_LABEL})`,
          `files are not supported documents (${SUPPORTED_DOCUMENT_TYPES_LABEL})`));
      return;
    }

    inputFiles.forEach(file => this.addFileItem(file));
  }

  addFileItem(file: File) {
    const item = new WarrantUploadFileItemState(this.store, { file });
    this.stagedItems.push(item);
    if (this.speciality === JobSpeciality.WarrantFirstDraft)
      this.store.documentUploadGuidedTraining.handleDocumentFileSelected('warrant');
    // First staged document defaults to the patrol narrative; the user can reassign.
    if (!this.primaryItemId)
      this.primaryItemId = item.id;
  }

  removeFileItem(id: string) {
    const itemIndex = this.stagedItems.findIndex(item => item.id === id);
    if (itemIndex > -1)
      this.stagedItems.splice(itemIndex, 1);
    if (this.primaryItemId === id)
      this.primaryItemId = this.stagedItems[0]?.id ?? null;
  }

  async submit(): Promise<string | null> {
    if (this.isSubmitDisabled)
      return null;

    this.isSubmitting = true;

    const teamId = this.teamId || undefined;
    // One subpoena per records holder, each from its own upload of the same documents.
    const holders: (string | undefined)[] = this.isSubpoena ? this.recordsHolders : [undefined];
    const jobs: JobModel[] = [];
    for (const [index, holder] of holders.entries()) {
      const job = await this.startJob(teamId, holder);
      if (!job) {
        // Keep only the holders not started yet, so a retry creates no duplicates.
        runInAction(() => {
          this.holderInputs.clear();
          this.wholeHolders.clear();
          // Each remaining holder is already split: keep it whole so a retry doesn't split it again.
          holders.slice(index).forEach(remaining => this.addHolder(remaining, true));
        });
        this.isSubmitting = false;
        return null;
      }
      jobs.push(job);
    }

    this.reset();
    this.isSubmitting = false;
    if (this.speciality === JobSpeciality.WarrantFirstDraft)
      this.store.documentUploadGuidedTraining.handleWorkflowDocumentStarted('warrant', jobs[0].id, teamId ?? null);
    if (jobs.length > 1) {
      notifySuccess(this, `Started ${jobs.length} subpoena requests, one per records holder.`);
      // The library lists every version; opening the first one alone hid the others.
      this.store.history.push({
        pathname: teamId ? Routes.teamDashboard(teamId) : Routes.userDashboard(),
        search: teamId && this.folderId ? `?folder=${this.folderId}` : undefined,
      });
      return jobs[0].id;
    }

    const { state, ...to } = BackRoutes[Routes.userDocument()].route({ id: jobs[0].id, teamId });
    this.store.history.push(to, state);
    return jobs[0].id;
  }

  private async startJob(teamId: string | undefined, holder?: string): Promise<JobModel | null> {
    const primaryItem = this.stagedItems.find(item => this.isPrimary(item.id)) ?? this.stagedItems[0];
    const supplementalItems = this.stagedItems.filter(item => item.id !== primaryItem.id);

    // Upload the primary narrative first so the server mints the shared job id; every
    // supplemental then uploads under that id at {dataLocation}/document/source/{index}-{name}.
    const [primaryTask] = this.store.uploadService.createTasks(
      [{ file: primaryItem.file, jobSpeciality: this.speciality, uploadOnly: true, partIndex: 0 }],
      { teamId },
    );
    await when(() => primaryTask.isCompleted || primaryTask.isFailed);

    const primaryToken = primaryTask.uploadedToken;
    const jobId = jobIdFromToken(primaryToken);

    if (primaryTask.isFailed || !primaryToken || !jobId) {
      this.dispatch('NotificationService', 'notifyError', this.failedMessage(holder));
      return null;
    }

    const supplementalTasks = this.store.uploadService.createTasks(
      supplementalItems.map((item, index) => ({
        file: item.file,
        jobSpeciality: this.speciality,
        uploadOnly: true,
        partIndex: index + 1,
      })),
      { teamId, jobId },
    );

    await Promise.all(supplementalTasks.map(task => when(() => task.isCompleted || task.isFailed)));

    if (supplementalTasks.some(task => task.isFailed)) {
      this.dispatch('NotificationService', 'notifyError', 'Failed to upload one or more documents.');
      return null;
    }

    const inputDocumentLocations = [primaryTask, ...supplementalTasks]
      .map(task => recordingLocationFromToken(task.uploadedToken))
      .filter((location): location is string => !!location);

    const synchronizer = new JobSynchronizer({
      teamId,
      jobData: {
        speciality: this.speciality,
        folderId: this.folderId ?? undefined,
        tags: this.tagsInput.value?.trim() || undefined,
        inputDocumentLocations,
        ...holder
          ? {
            title: `${holderName(holder)} – ${primaryItem.file.name.replace(/\.[^.]+$/, '')}`,
            subpoenaRequest: {
              recipient: holder,
              requestDate: new Date().toLocaleDateString('en-US', { dateStyle: 'long' }),
            },
          }
          : { detectiveInsights: this.detectiveInsightsInput.value || undefined },
        sourceJobIds: this.linkVideos ? [...this.linkedVideoIds] : undefined,
      },
    }, this.store);
    synchronizer.bindToken(primaryToken);

    const [job, jobErr] = await synchronizer.submitStartJob();

    if (jobErr || !job) {
      this.dispatch('NotificationService', 'notifyError', this.failedMessage(holder));
      return null;
    }
    return job;
  }

  reset() {
    this.stagedItems.clear();
    this.primaryItemId = null;
    this.detectiveInsightsInput.clear();
    this.holderInputs.clear();
    this.wholeHolders.clear();
    this.addHolder();
    this.tagsInput.clear();
    this.linkVideos = false;
    this.linkedVideoIds.clear();
  }

  private failedMessage(holder?: string): string {
    if (holder) return `Failed to start subpoena request for ${holderName(holder)}.`;
    return this.isInvestigative ? 'Failed to start investigative draft.' : 'Failed to start warrant narrative.';
  }
}
